/**
 * Where to send the user once they have signed in.
 *
 * Signing in leaves the application entirely — the browser goes to Okta and comes back on
 * /login/callback — so the page that was asked for has to be written down somewhere that survives
 * the round trip. Without this, following a link to a specific request from an email while signed
 * out landed on the landing page, and the request had to be found by hand.
 *
 * Two things make the obvious storage choices wrong, and both were found the hard way:
 *
 *   * session storage is wiped on this very path — signing out, an expired token and the token
 *     manager's error handler all call sessionStorage.clear();
 *   * local storage survives, but it is shared by every tab of the browser. An email link opens a
 *     new tab while the old one is still on the application; the old tab reaches the landing page
 *     first, takes the destination meant for the new one and goes there itself, leaving the tab
 *     the reader is actually looking at with nothing.
 *
 * So the entry lives in local storage under a key belonging to this tab. The tab is identified by
 * window.name, which no code here touches and which survives a redirect through the identity
 * provider — unlike session storage, and unlike anything else that is per-tab.
 */

import { oktaAuth } from "./oktaAuth"

const KEY_PREFIX = "postLoginRedirect:"
const SHARED_KEY = "postLoginRedirect"
const TAB_NAME_PREFIX = "edt-tab-"

// Temporary, while this flow is being proved out on real logins: see trace().
const TRACE_KEY = "deepLinkTrace"

// Long enough for a login that needs a password reset or a second factor; short enough that a
// forgotten destination is not waiting the next morning.
const MaximumAgeMs = 15 * 60 * 1000

interface RememberedPath {
	path: string
	at: number
	/**
	 * How many times this destination has been handed out. It is deliberately not deleted when it
	 * is read — see peekPostLoginPath — so this is what stops a destination that can never be
	 * reached from bouncing the reader around for ever.
	 */
	attempts?: number
	/**
	 * When the reader actually got there. The entry is kept for a short while afterwards, because
	 * signing in can have more than one round trip in the air at once: a second one landing after
	 * the destination has been reached would otherwise drop the reader on the landing page.
	 */
	deliveredAt?: number
}

/**
 * How long a delivered destination is still honoured.
 *
 * It exists for one thing: a second sign-in round trip landing after the first has already
 * delivered the reader. Those arrive a second or two later, never more. The window is kept close to
 * that because it cannot tell a stray navigation from a deliberate one — open the site root inside
 * it and you are sent back to the page you had just been taken to, instead of the landing page.
 */
const DeliveredGraceMs = 8 * 1000

/**
 * After this many attempts the destination is abandoned and the reader stays on the landing page.
 * Three covers the honest cases - a callback plus a second sign-in round trip - without turning an
 * unreachable page into a loop.
 */
const MaximumAttempts = 3

/**
 * Remembers an in-application path for this tab. Anything else is ignored: a stored value that is
 * not clearly one of our own paths is exactly how a login flow turns into an open redirect.
 */
export function rememberPostLoginPath(path: string | undefined | null): void {
	if (!isInternalPath(path)) return

	const entry = JSON.stringify({ path, at: Date.now() } as RememberedPath)

	// Written twice: once for this tab, once shared.
	//
	// The tab's own copy is what a reader who stays in one tab gets back, and no other tab can
	// take it. But the sign-in does not always finish where it started — logging out leaves a tab
	// sitting on the login screen, and that tab can be the one that completes the login when the
	// identity provider comes back. It has no copy of its own, so it falls back to the shared one.
	localStorage.setItem(storageKey(), entry)
	localStorage.setItem(SHARED_KEY, entry)

	removeExpiredEntries()
	trace("remembered", path)
}

/**
 * Hands the same destination to the Okta SDK, which keeps it with the login transaction it is
 * about to start — a second, independent route home for the same value.
 */
export function rememberOriginalUriWithOkta(path: string | undefined | null): void {
	if (!isInternalPath(path)) return

	try {
		oktaAuth.setOriginalUri(path)
	} catch {
		// This channel is a bonus: losing it leaves the stored one.
	}
}

/**
 * What the Okta SDK kept, if anything. Like peekPostLoginPath it does not clear: the destination
 * is dropped on arrival, by the guard of the page itself.
 */
export function peekOriginalUriFromOkta(): string | null {
	try {
		const uri = oktaAuth.getOriginalUri()

		if (!uri) return null

		// The SDK returns whatever it was given, which may be a full address.
		const path = uri.startsWith("http") ? new URL(uri).pathname + new URL(uri).search : uri
		if (!isInternalPath(path)) return null

		trace("restored (okta)", path)
		return path
	} catch {
		return null
	}
}

export function forgetPostLoginPath(): void {
	if (localStorage.getItem(storageKey()) || localStorage.getItem(SHARED_KEY)) {
		trace("forgotten", location.pathname)
	}

	localStorage.removeItem(storageKey())
	localStorage.removeItem(SHARED_KEY)

	try {
		oktaAuth.removeOriginalUri()
	} catch {
		// Nothing kept there, or no transaction in progress.
	}
}

/**
 * The destination still to be reached: read plainly, nothing cleared, no attempt counted, and
 * nothing returned once it has been delivered.
 *
 * For code that needs to know where the reader is headed without taking part in the journey — the
 * login screen deciding what to navigate to, and the arrival check in the router.
 *
 * Nothing here deletes: signing in is not one hop, and the destination used to be deleted when the
 * journey began rather than when it ended. The callback screen would navigate to it, a guard would
 * find no session yet and send the browser back to the identity provider, and the second time round
 * there was nothing left to read — the reader saw the address they wanted flash past and landed on
 * the landing page.
 */
export function readPostLoginPath(): string | null {
	const entry = storedEntry()
	return entry && !entry.deliveredAt ? entry.path : null
}

/**
 * Marks the destination as reached, keeping it for a short grace period.
 *
 * Deleting it here is what kept breaking the flow: signing in is not one round trip. A second one
 * can already be in the air when the first delivers the reader, and it lands a second later on the
 * login screen with nothing to go on — so it falls back to the landing page and undoes the arrival.
 * Kept for a moment, that latecomer is sent back where the reader was going.
 */
export function markPostLoginPathDelivered(): void {
	const entry = storedEntry()
	if (!entry || entry.deliveredAt) return

	const delivered = JSON.stringify({ ...entry, deliveredAt: Date.now() } as RememberedPath)
	if (localStorage.getItem(storageKey())) localStorage.setItem(storageKey(), delivered)
	if (localStorage.getItem(SHARED_KEY)) localStorage.setItem(SHARED_KEY, delivered)

	// The Okta SDK keeps its own copy of the same destination and restores it on its own
	// initiative, without passing through anything here. Left behind, it kept sending the reader
	// to the page they had already been taken to.
	try {
		oktaAuth.removeOriginalUri()
	} catch {
		// Nothing kept there, or no transaction in progress.
	}

	trace("delivered", entry.path)
}

/**
 * A destination that was reached moments ago, for as long as a stray navigation could still be on
 * its way. This is what the landing page checks before settling for itself.
 */
export function recentlyDeliveredPath(): string | null {
	const entry = storedEntry()
	if (!entry?.deliveredAt) return null
	if (Date.now() - entry.deliveredAt > DeliveredGraceMs) return null

	return entry.path
}

/** The entry as stored, valid and unexpired, or null. */
function storedEntry(): RememberedPath | null {
	const stored = localStorage.getItem(storageKey()) ?? localStorage.getItem(SHARED_KEY)
	if (!stored) return null

	try {
		const entry = JSON.parse(stored) as RememberedPath
		if (!isInternalPath(entry?.path)) return null
		if (!entry.at || Date.now() - entry.at > MaximumAgeMs) return null
		// Delivered and past its grace: the journey is over, and the entry must not resurface
		// later — a bare visit to the site would otherwise be answered with the last email link.
		if (entry.deliveredAt && Date.now() - entry.deliveredAt > DeliveredGraceMs) return null

		return entry
	} catch {
		return null
	}
}

export function peekPostLoginPath(): string | null {
	// Shares storedEntry with the other readers — which is also where the tab's own copy is
	// preferred over the shared one, and where an expired entry is discarded.
	const entry = storedEntry()
	if (!entry) return null

	// Already reached: this is no longer a destination to travel to. Only the short grace
	// window still honours it, and that is recentlyDeliveredPath's job, not this one. Without
	// this the entry was handed out again on every later visit, so opening the bare domain
	// answered with the page from the last email link instead of the landing page.
	if (entry.deliveredAt) return null

	try {
		const attempts = (entry.attempts ?? 0) + 1
		if (attempts > MaximumAttempts) {
			trace("given up after " + MaximumAttempts + " attempts", entry.path)
			forgetPostLoginPath()
			return null
		}

		const counted = JSON.stringify({ ...entry, attempts } as RememberedPath)
		if (localStorage.getItem(storageKey())) localStorage.setItem(storageKey(), counted)
		if (localStorage.getItem(SHARED_KEY)) localStorage.setItem(SHARED_KEY, counted)

		trace(`restored (attempt ${attempts})`, entry.path)
		return entry.path
	} catch {
		return null
	}
}

/**
 * One line per decision, so a login that lands in the wrong place can be read off the console
 * instead of guessed at. Deliberately quiet: three lines at most per sign-in.
 */
function trace(what: string, path: string): void {
	const line = `${new Date().toISOString().slice(11, 19)} ${what}: ${path} (tab ${tabId()}, on ${location.pathname})`
	console.info(`[deep link] ${line}`)

	// Also kept on disk, because the interesting moments are either side of a redirect and the
	// console is emptied by every page load. Read it with:
	//   JSON.parse(localStorage.deepLinkTrace).forEach(l => console.log(l))
	try {
		const history = JSON.parse(localStorage.getItem(TRACE_KEY) ?? "[]") as string[]
		history.push(line)
		localStorage.setItem(TRACE_KEY, JSON.stringify(history.slice(-20)))
	} catch {
		localStorage.setItem(TRACE_KEY, JSON.stringify([line]))
	}
}

/**
 * A name for this tab, made up on first use. window.name is empty for a tab the browser opened
 * fresh and is carried across navigations of that tab, including the departure to the identity
 * provider and the return — which is exactly the lifetime needed here.
 */
function tabId(): string {
	if (!window.name || !window.name.startsWith(TAB_NAME_PREFIX)) {
		window.name = TAB_NAME_PREFIX + Math.random().toString(36).slice(2, 10)
	}

	return window.name
}

function storageKey(): string {
	return KEY_PREFIX + tabId()
}

/**
 * Clears out destinations left by tabs that were closed mid-login. Without this the entries would
 * accumulate, one per tab, for as long as the browser keeps its storage.
 */
function removeExpiredEntries(): void {
	const now = Date.now()

	for (const key of Object.keys(localStorage)) {
		if (!key.startsWith(KEY_PREFIX) && key !== SHARED_KEY) continue

		try {
			const entry = JSON.parse(localStorage.getItem(key) ?? "") as RememberedPath
			if (!entry?.at || now - entry.at > MaximumAgeMs) localStorage.removeItem(key)
		} catch {
			localStorage.removeItem(key)
		}
	}
}

/**
 * A path of this application: one leading slash, and not the "//host" form a browser reads as an
 * address somewhere else entirely.
 */
function isInternalPath(path: string | undefined | null): path is string {
	if (!path) return false
	if (!path.startsWith("/") || path.startsWith("//")) return false

	// A destination worth coming back to is a page INSIDE the application, so it has to
	// sit under /app/. Everything else is either where the sign-in itself happens or the
	// place a sign-in ends up anyway:
	//
	//   "/" and "/login"  the login screen. Remembered, it made the login screen send the
	//                     reader to the login screen: opening the site root without /app
	//                     left them sitting there, apparently stuck.
	//   "/login/callback" the hand-over from the identity provider; returning to it loops.
	//   "/app", "/app/"   the landing page, which is the fallback already.
	//
	// It doubles as the open-redirect guard: only our own deep links are ever stored.
	return path.startsWith("/app/") && path !== "/app/"
}
